/**
 * API Deduplication Utility
 * Prevents duplicate parallel API calls for the same endpoint.
 * If the same API call is made multiple times in parallel, only one request is sent
 * and all callers share the same promise.
 */

const inFlightRequests = new Map();

/**
 * Creates a unique key for a request based on method and URL
 */
const getRequestKey = (method, url, params = null) => {
  let key = `${method.toUpperCase()}:${url}`;
  if (params) {
    // Include query params in the key if present
    const paramString = Object.keys(params)
      .sort()
      .map(k => `${k}=${JSON.stringify(params[k])}`)
      .join('&');
    key += `?${paramString}`;
  }
  return key;
};

/**
 * Wraps an API call with deduplication logic
 * @param {Function} apiCall - The actual API call function
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} url - API endpoint URL
 * @param {Object} params - Query parameters or request body
 * @returns {Promise} The API response
 */
export const withDeduplication = async (apiCall, method, url, params = null) => {
  const key = getRequestKey(method, url, params);

  // If a request with this key is already in flight, return its promise
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  // Create and store the promise
  const promise = apiCall()
    .finally(() => {
      // Remove from in-flight map when complete (success or failure)
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, promise);
  return promise;
};

/**
 * Clears all in-flight requests (useful for logout or app cleanup)
 */
export const clearInFlightRequests = () => {
  inFlightRequests.clear();
};

/**
 * Get count of in-flight requests (for debugging)
 */
export const getInFlightCount = () => {
  return inFlightRequests.size;
};

export default { withDeduplication, clearInFlightRequests, getInFlightCount };
